import { useSirenControllerSearchSiren } from 'korint-sdk';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@folio/components/ui';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

const fields = [
  'name',
  'siren',
  'legalStatus.1',
  'location.address',
  'location.city',
  'location.postCode',
  'location.country',
  'nafCode',
  'nafLabel',
  'creationDate',
] as const;

type CompanyInformationProps = {
  siret?: string;
};

const isSiretFormat = (s: string | undefined): s is string => !!s && /^\d{14}$/.test(s);

export function CompanyInformation({ siret }: CompanyInformationProps) {
  const { t } = useTranslation();
  // The kbis prefill can land in this prop as the company name (not a real
  // 14-digit SIRET) before the SIREN lookup has resolved. Skip the lookup
  // until the value is a real SIRET to avoid INVALID_CUSTOMER_SIRET 400s.
  const hasValidSiret = isSiretFormat(siret);
  const { data, isLoading } = useSirenControllerSearchSiren(
    { siret },
    { query: { enabled: hasValidSiret } },
  );

  const companyInfo = data?.data?.[0];

  if (isLoading || !companyInfo) {
    return null;
  }

  const isClosed = _.get(companyInfo, 'isActive') === false;

  return (
    <Card>
      <CardHeader>
        <Group justify="between" gap={2}>
          <CardTitle>{t('companyInformation.title')}</CardTitle>
          {isClosed && (
            <Text
              inline
              size="xs"
              weight="semibold"
              className="rounded-full bg-red-100 px-3 py-1 uppercase text-red-700"
            >
              {t('companyInformation.closed')}
            </Text>
          )}
        </Group>
      </CardHeader>
      <CardContent>
        <Table>
          <TableBody>
            {fields.map(field => {
              const value = _.get(companyInfo, field);

              return (
                <TableRow key={field}>
                  <TableHead className="text-left w-1/3">
                    {t(`companyInformation.fields.${field}`)}
                  </TableHead>
                  <TableCell className="text-left">
                    {value ? (
                      <div>{value}</div>
                    ) : (
                      <div className="italic text-muted-foreground">{t('common.unavailable')}</div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
